import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { END_USER_APP_HOST } from '../../config';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { publicPlanEmbedCode, publicPlanUrl } from './publicPlan';

interface Props {
    categoryKey: string;
    categoryName: string;
    /** The tenant's slug — the booking app is served at `<host>/<slug>`. */
    slug: string;
    /** Whether the booking app is live; the plan shows nothing before. */
    active: boolean;
}

/**
 * #3075: the public plan of one space category, to put on the tenant's own
 * website — as a link, or embedded with an iframe. Both are copied, not
 * typed: the address carries the category key, which nobody should retype.
 */
const PublicPlanLinks = ({ categoryKey, categoryName, slug, active }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    if (!slug) return null;

    const url = publicPlanUrl(END_USER_APP_HOST, slug, categoryKey);
    const copy = async (text: string, doneKey: string) => {
        try {
            await navigator.clipboard.writeText(text);
            dispatch(enqueueSuccessSnackbar({ message: t(doneKey) }));
        } catch {
            // Said, not swallowed: an operator who believes it was copied
            // pastes the previous clipboard into the website.
            dispatch(enqueueErrorSnackbar({ message: t('services.public_plan.copy_failed') }));
        }
    };

    return (
        <p className={'text-xs text-foreground/60'}>
            {t('services.public_plan.label')}{' '}
            <a href={url} target={'_blank'} rel={'noreferrer'} className={'text-primary underline'}>
                {t('services.public_plan.open')}
            </a>
            {' · '}
            <button type={'button'} className={'text-primary underline'} onClick={() => copy(url, 'services.public_plan.link_copied')}>
                {t('services.public_plan.copy_link')}
            </button>
            {' · '}
            <button
                type={'button'}
                className={'text-primary underline'}
                onClick={() =>
                    copy(
                        publicPlanEmbedCode(url, t('services.public_plan.frame_title', { name: categoryName })),
                        'services.public_plan.code_copied',
                    )
                }
            >
                {t('services.public_plan.copy_code')}
            </button>
            {!active && <span className={'block'}>{t('services.public_plan.not_live')}</span>}
        </p>
    );
};

export default PublicPlanLinks;
